import { Form, Modal, Select, Spin, Typography, Space, Button } from 'antd';
import { useEffect, useState } from 'react';
import { DownloadOutlined } from '@ant-design/icons';
import {
  getCompanies,
  getLogFiles,
  downloadLogFile,
} from '../../utils/httpRequest';

export interface DownloadLogModalProps {
  open: boolean;
  onClose: () => void;
}

interface LogFile {
  scope: string;
  fileName: string;
  type: string;
}

const DownloadLogModal = ({ open, onClose }: DownloadLogModalProps) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<
    { companyID: string; name: string }[]
  >([]);
  const [fetchingCompanies, setFetchingCompanies] = useState(false);
  const [logFiles, setLogFiles] = useState<LogFile[]>([]);
  const [fetchingFiles, setFetchingFiles] = useState(false);
  const [selectedScope, setSelectedScope] = useState<string>('');

  const fetchCompaniesData = async () => {
    try {
      setFetchingCompanies(true);
      const data = await getCompanies();
      setCompanies(data || []);
    } catch (err) {
      console.error('Failed to fetch companies:', err);
    } finally {
      setFetchingCompanies(false);
    }
  };

  const fetchLogFiles = async (scope: string) => {
    try {
      setFetchingFiles(true);
      const data = await getLogFiles(scope);
      setLogFiles(data || []);
    } catch (err) {
      console.error('Failed to fetch log files:', err);
    } finally {
      setFetchingFiles(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchCompaniesData();
      fetchLogFiles('companies-all');
      setSelectedScope('companies-all');
      form.setFieldsValue({ companyName: 'companies-all' });
    } else {
      form.resetFields();
      setLogFiles([]);
      setSelectedScope('');
    }
  }, [open]);

  const handleCompanyChange = (value: string) => {
    form.setFieldValue('fileName', undefined);
    setSelectedScope(value);
    fetchLogFiles(value);
  };

  const handleDownload = async () => {
    try {
      setLoading(true);
      const { fileName } = await form.validateFields();

      const { blob, fileName: downloadName } = await downloadLogFile(fileName);

      const blobObj = new Blob([blob], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blobObj);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      onClose();
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const companyOptions = [
    { label: 'All Companies', value: 'companies-all' },
    ...companies.map((c) => ({
      label: c.name,
      value: c.name,
    })),
  ];

  const fileOptions = logFiles.map((f) => ({
    label: f.fileName,
    value: f.fileName,
  }));

  return (
    <Modal
      open={open}
      title="Download Company Logs"
      onCancel={onClose}
      onClose={onClose}
      footer={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            loading={loading}
          >
            Download
          </Button>
        </Space>
      }
    >
      <Spin spinning={fetchingCompanies}>
        <Typography.Paragraph type="secondary">
          Select a company to choose a log file to download.
        </Typography.Paragraph>
        <Form form={form} layout="vertical">
          <Form.Item
            label="Company"
            name="companyName"
            rules={[{ required: true, message: 'Please select a company' }]}
          >
            <Select
              placeholder="Select a company"
              showSearch
              optionFilterProp="label"
              options={companyOptions}
              onChange={handleCompanyChange}
            />
          </Form.Item>
          <Form.Item
            label="Log File"
            name="fileName"
            rules={[{ required: true, message: 'Please select a log file' }]}
          >
            <Select
              placeholder="Select a log file"
              showSearch
              optionFilterProp="label"
              options={fileOptions}
              loading={fetchingFiles}
              disabled={!selectedScope || fetchingFiles}
              notFoundContent={
                fetchingFiles ? <Spin size="small" /> : 'No files'
              }
            />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default DownloadLogModal;
